import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Target, CheckCircle2, Circle, ChevronRight, Sparkles, Calendar } from 'lucide-react'
import { motion } from 'framer-motion'
import { Card, CardContent, Button } from '@/components/ui'
import { useFamily } from '@/context/FamilyContext'
import {
  getTodaysMission,
  startMission,
  getWordGoalForGrade,
  getTodayMissionType,
  type MissionStatus,
} from '@/services/missionService'
import { createStory } from '@/services/storyService'
import type { MissionChecklistItem } from '@/types/database'

export function MissionsPanel() {
  const navigate = useNavigate()
  const { activeWriter } = useFamily()
  const [missionStatus, setMissionStatus] = useState<MissionStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isStarting, setIsStarting] = useState(false)

  const dayType = getTodayMissionType()

  useEffect(() => {
    if (activeWriter) {
      loadTodaysMission()
    }
  }, [activeWriter])

  async function loadTodaysMission() {
    if (!activeWriter) return
    setIsLoading(true)
    try {
      const status = await getTodaysMission(activeWriter.id, activeWriter.age_range)
      setMissionStatus(status)
    } catch (err) {
      console.error('Error loading mission:', err)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleStartMission() {
    if (!activeWriter || !missionStatus) return

    setIsStarting(true)
    try {
      // Create a new story for this mission
      const story = await createStory(activeWriter.id, missionStatus.mission.title)
      if (!story) {
        console.error('Failed to create story')
        return
      }

      // Start the mission
      const result = await startMission(activeWriter.id, missionStatus.mission.id)
      if (!result.success) {
        console.error('Failed to start mission:', result.error)
        return
      }

      // Navigate to write with story
      navigate(`/stories/${story.id}`)
    } catch (err) {
      console.error('Error starting mission:', err)
    } finally {
      setIsStarting(false)
    }
  }

  // Loading state
  if (isLoading) {
    return <MissionsPanelSkeleton />
  }

  // Weekend - free day
  if (dayType.type === 'weekend') {
    return <FreeWritingPanel />
  }

  // No mission found (shouldn't happen on weekdays, but handle gracefully)
  if (!missionStatus) {
    return <FreeWritingPanel />
  }

  const { mission, isCompleted, isStarted, wordCount } = missionStatus
  const wordGoal = getWordGoalForGrade(mission, activeWriter?.age_range || null)
  const progress = wordGoal > 0 ? Math.min((wordCount / wordGoal) * 100, 100) : 0
  const checklist = (mission.checklist || []) as MissionChecklistItem[]

  return (
    <Card className="h-full">
      <CardContent className="p-8">
        {/* Track badge */}
        <div className="flex items-center gap-3 mb-4">
          <Target className="w-8 h-8 text-[var(--color-kids-secondary)]" />
          <div className="flex-1">
            <span className="text-sm font-semibold text-[var(--color-kids-secondary)] uppercase tracking-wide">
              {dayType.type === 'friday' ? "Friday's Choice" : "Today's Mission"}
            </span>
            {mission.track && (
              <span className="text-xs text-[var(--color-kids-text-muted)] ml-2">
                {dayType.label}
              </span>
            )}
          </div>
          {isCompleted && <CheckCircle2 className="w-6 h-6 text-green-500" />}
        </div>

        {/* Title and description */}
        <h2 className="font-display text-xl font-bold text-[var(--color-kids-text)] mb-2">
          {mission.title}
        </h2>
        <p className="text-[var(--color-kids-text-muted)] mb-4">{mission.description}</p>

        {/* Checklist preview (show first 3 items) */}
        {checklist.length > 0 && (
          <div className="space-y-2 mb-4">
            {checklist.slice(0, 3).map((item) => (
              <div key={item.id} className="flex items-center gap-2 text-sm">
                <Circle className="w-4 h-4 text-[var(--color-kids-text-muted)]" />
                <span className="text-[var(--color-kids-text)]">{item.label}</span>
                {item.required && (
                  <span className="text-xs text-[var(--color-kids-secondary)]">*</span>
                )}
              </div>
            ))}
            {checklist.length > 3 && (
              <p className="text-xs text-[var(--color-kids-text-muted)] pl-6">
                +{checklist.length - 3} more items
              </p>
            )}
          </div>
        )}

        {/* Word progress bar */}
        {wordGoal > 0 && (
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-2 bg-[var(--color-kids-surface-alt)] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-[var(--color-kids-secondary)] rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <span className="text-sm text-[var(--color-kids-text-muted)] whitespace-nowrap">
              {wordCount}/{wordGoal} words
            </span>
          </div>
        )}

        {/* Action button */}
        {isCompleted ? (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 text-green-700 mb-2">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Done for today!</span>
            </div>
            <p className="text-sm text-[var(--color-kids-text-muted)]">
              Come back tomorrow for a new mission
            </p>
          </div>
        ) : isStarted ? (
          <Link to="/stories" className="block">
            <Button className="w-full">
              Continue Mission
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        ) : (
          <Button onClick={handleStartMission} disabled={isStarting} className="w-full">
            {isStarting ? (
              'Starting...'
            ) : (
              <>
                Start Mission
                <Sparkles className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        )}

        {/* Stretch goal */}
        {mission.stretch_goal && !isCompleted && (
          <p className="text-xs text-[var(--color-kids-text-muted)] mt-4 text-center italic">
            Stretch goal: {mission.stretch_goal}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function MissionsPanelSkeleton() {
  return (
    <Card className="h-full">
      <CardContent className="p-8">
        <div className="animate-pulse">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-1/3" />
          </div>
          <div className="h-6 bg-gray-200 rounded w-2/3 mb-2" />
          <div className="h-4 bg-gray-200 rounded w-full mb-4" />
          <div className="space-y-2 mb-4">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-2/3" />
          </div>
          <div className="h-2 bg-gray-200 rounded w-full mb-6" />
          <div className="h-10 bg-gray-200 rounded w-full" />
        </div>
      </CardContent>
    </Card>
  )
}

function FreeWritingPanel() {
  return (
    <Card className="h-full">
      <CardContent className="p-8 text-center">
        <Calendar className="w-12 h-12 text-[var(--color-kids-primary)] mx-auto mb-4 opacity-70" />
        <h2 className="font-display text-xl font-bold text-[var(--color-kids-text)] mb-2">
          Free Writing Day!
        </h2>
        <p className="text-[var(--color-kids-text-muted)] mb-6">
          No mission today. Write whatever inspires you!
        </p>
        <Link to="/stories">
          <Button variant="secondary">
            Go to My Stories
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  )
}
